import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import React, { useState, useEffect } from 'react';

import ProjectTable from './project-table';
import SearchAndFiltersBar from '../../base/search-and-filters-bar';
import Spacer, { spc } from '../../base/spacer';
import Text from '../../base/text/index';
import { useAIResourcesContext } from '../../project/dashboard/ai-resources-context';

import AnchUseCasesListContent from '~/client/components/use-case/anch-use-cases-list-content';
import OrganizationSelector from '~/client/components/use-case/organization-selector';
import { useAnchContext } from '~/client/contexts/anch-context';
import { useDebounce } from '~/client/hooks/policy';
import { dashboardRouteUtil } from '~/client/routes';
import * as GQL from '~/graphql/generated/client';
import Button from '~c/components/base/button';
import { CapabilityAuth } from '~c/components/capability-auth';

const COMPLIANCE_TAB_KEY = 'compliance_active_project_tab';

const ComplianceProjects: React.FC<{ className?: string }> = ({
  className,
}) => {
  const [activeProjectTab, setActiveProjectTab] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(COMPLIANCE_TAB_KEY);
      if (stored === 'compliance-monitor' || stored === 'ai-risk-navigator') {
        return stored;
      }
    }
    return 'compliance-monitor';
  });

  useEffect(() => {
    localStorage.setItem(COMPLIANCE_TAB_KEY, activeProjectTab);
  }, [activeProjectTab]);

  const {
    organizations,
    isConnected: anchConnected,
    loading: anchLoading,
    customerId,
    selectedOrganizationId,
    setSelectedOrganizationId,
  } = useAnchContext();

  const {
    aiSystemList: data,
    aiSystemPage,
    setAiSystemPage,
    aiSystemPageSize,
    setAiSystemPageSize,
    setAiSystemSearch,
    refetchAiSystemList,
    riskFilter,
    setRiskFilter,
    sortDirection,
    setSortDirection,
  } = useAIResourcesContext();

  useEffect(() => {
    refetchAiSystemList();
  }, []);

  const [projectsSearchInput, setProjectsSearchInput] = useState('');
  const debouncedProjectsSearch = useDebounce(projectsSearchInput, 500);

  useEffect(() => {
    setAiSystemSearch(debouncedProjectsSearch);
  }, [debouncedProjectsSearch, setAiSystemSearch]);

  useEffect(() => {
    setAiSystemPage(0);
  }, [riskFilter, setAiSystemPage]);

  const { navigateTo } = dashboardRouteUtil.useHelper();

  return (
    <div className={className}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Text sz="title">Projects</Text>
        <CapabilityAuth capability="org:create_project">
          <Button onClick={() => navigateTo('CREATE_AI_SYSTEM')}>
            Add project
          </Button>
        </CapabilityAuth>
      </Box>
      <Spacer pb={spc.xs} />
      <ProjectTable
        searchSlot={
          <SearchAndFiltersBar
            placeholder="Search projects..."
            value={projectsSearchInput}
            onChange={setProjectsSearchInput}
            onSearchChange={() => setAiSystemPage(0)}
          >
            <FormControl size="small" className="filter-field">
              <InputLabel>Risk</InputLabel>
              <Select
                value={riskFilter ?? GQL.RiskFilter.All}
                label="Risk"
                onChange={(e) =>
                  setRiskFilter(
                    (e.target.value as GQL.RiskFilter) || GQL.RiskFilter.All
                  )
                }
              >
                <MenuItem value={GQL.RiskFilter.All}>All</MenuItem>
                <MenuItem value={GQL.RiskFilter.High}>High</MenuItem>
                <MenuItem value={GQL.RiskFilter.Medium}>Medium</MenuItem>
                <MenuItem value={GQL.RiskFilter.Low}>Low</MenuItem>
              </Select>
            </FormControl>
          </SearchAndFiltersBar>
        }
        projectListData={
          data?.aiSystemList.projects.map((p) => ({
            id: p._id,
            projectName: p.name,
            source: p.source ?? 'Azure',
            risk: p.riskStatus ?? GQL.AssessmentRiskStatusType.High,
            assessmentCompletion: p.completionPercent ?? 0,
            policyAlignment:
              p.alignmentRiskStatus ?? GQL.AssessmentRiskStatusType.Unavailable,
            numControls: p.numControls ?? 0,
            numTestScores: p.numTestScores ?? 0,
            securityStatus: p.securityStatus ?? GQL.AssessmentCategoryStatus.NA,
            privacyStatus: p.privacyStatus ?? GQL.AssessmentCategoryStatus.NA,
            biasStatus: p.biasStatus ?? GQL.AssessmentCategoryStatus.NA,
            performanceStatus:
              p.performanceStatus ?? GQL.AssessmentCategoryStatus.NA,
            assessmentStatusByPolicy: (p.assessmentStatusByPolicy ?? []).map(
              (s) => ({
                policyIdentifier: s.policyIdentifier,
                status: s.status ?? null,
              })
            ),
            sharedWith: (p.sharedWith ?? []).filter(
              (m): m is GQL.SharedMembers => m !== null
            ),
            isDraft: p.isDraft ?? false,
          })) ?? []
        }
        page={aiSystemPage}
        onPageChange={setAiSystemPage}
        rowsPerPage={aiSystemPageSize}
        onRowsPerPageChange={setAiSystemPageSize}
        totalCount={data?.aiSystemList.totalCount ?? 0}
        sortDirection={sortDirection}
        onSortChange={setSortDirection}
        riskFilter={riskFilter}
        onRiskSortChange={setRiskFilter}
        tabsConfig={
          anchConnected && !anchLoading && organizations.length > 0
            ? {
                tabs: [
                  {
                    label: 'AI Compliance Monitor',
                    value: 'compliance-monitor',
                  },
                  { label: 'AI Risk Navigator', value: 'ai-risk-navigator' },
                ],
                activeTab: activeProjectTab,
                onTabChange: setActiveProjectTab,
                alternateTabContent: selectedOrganizationId ? (
                  <AnchUseCasesListContent
                    organizationUnitId={selectedOrganizationId}
                    customerId={customerId}
                  />
                ) : null,
                alternateTabAction:
                  organizations.length > 1 ? (
                    <OrganizationSelector
                      organizations={organizations}
                      selectedOrganizationId={selectedOrganizationId}
                      onOrganizationChange={setSelectedOrganizationId}
                    />
                  ) : undefined,
              }
            : undefined
        }
      />
    </div>
  );
};

export default ComplianceProjects;
